// studioFind — find on this desk (pen "Studio – find on the desk"): a text
// and one filter over everything on the desk and inside its containers.
// Pure: the desk's items, which of them match, and what lights up on the
// canvas. Scope: this desk only (titles, briefs, kinds); Cmd+K stays global.

export type { FindFilter, FindItem };
export { FIND_FILTERS, findItems, findMatches, litCards };

import { exhaustSwitchCase } from "@bilda/utils";
import { docStateOf } from "../StudioDesk/deskFacts.js";
import type { ContainerPart, StudioCard } from "./studioCards.js";

type FindFilter =
    | "all"
    | "sources"
    | "materials"
    | "modules"
    | "empty"
    | "planned"
    | "ai";

const FIND_FILTERS: readonly FindFilter[] = [
    "all",
    "sources",
    "materials",
    "modules",
    "empty",
    "planned",
    "ai",
];

type FindKind = "source" | "material" | "module" | "course" | "other";

// One thing on the desk: a card, or a part inside a container card.
type FindItem = {
    readonly id: string;
    readonly title: string;
    readonly kind: FindKind;
    readonly brief: string;
    readonly planned: boolean;
    readonly empty: boolean;
    readonly ai: boolean;
    // The card on the canvas that holds it (itself, for a card), and the
    // path of containers it lies inside, outermost first (empty on the desk).
    readonly card: string;
    readonly inside: readonly { readonly id: string; readonly title: string }[];
};

const partItems = (
    part: ContainerPart,
    card: string,
    inside: FindItem["inside"],
): readonly FindItem[] => [
    {
        id: part.id,
        title: part.title,
        kind: part.kind,
        brief: part.document?.brief ?? "",
        planned: docStateOf(part) === "planned",
        empty: docStateOf(part) === "empty",
        ai: part.aiAuthored,
        card,
        inside,
    },
    ...part.parts.flatMap((p) =>
        partItems(p, card, [...inside, { id: part.id, title: part.title }]),
    ),
];

// Every card on the desk and every part inside a container, in desk order.
const findItems = (cards: readonly StudioCard[]): readonly FindItem[] =>
    cards.flatMap((c): readonly FindItem[] => {
        const base = { id: c.id, title: c.title, card: c.id, inside: [] };
        switch (c.type) {
            case "source":
            case "generic":
                return [
                    {
                        ...base,
                        kind: c.type === "source" ? "source" : "other",
                        brief: "",
                        planned: false,
                        empty: false,
                        ai: false,
                    },
                ];
            case "material": {
                const state = docStateOf({ ...c, kind: "material" });
                return [
                    {
                        ...base,
                        kind: "material",
                        brief: c.document?.brief ?? "",
                        planned: state === "planned",
                        empty: state === "empty",
                        ai: c.aiAuthored,
                    },
                ];
            }
            case "container":
                return [
                    {
                        ...base,
                        kind: c.kind === "course" ? "course" : "module",
                        brief: "",
                        planned: false,
                        empty: c.parts.length === 0,
                        ai: c.aiAuthored,
                    },
                    ...c.parts.flatMap((p) =>
                        partItems(p, c.id, [{ id: c.id, title: c.title }]),
                    ),
                ];
            default:
                return exhaustSwitchCase(c);
        }
    });

const passes = (item: FindItem, filter: FindFilter): boolean => {
    switch (filter) {
        case "all":
            return true;
        case "sources":
            return item.kind === "source";
        case "materials":
            return item.kind === "material";
        case "modules":
            return item.kind === "module" || item.kind === "course";
        case "empty":
            return item.empty;
        case "planned":
            return item.planned;
        case "ai":
            return item.ai;
        default:
            return exhaustSwitchCase(filter);
    }
};

// Text and filter combine (AND). No text and "All": nothing is being looked
// for, so nothing matches (nothing fades).
const findMatches = (
    items: readonly FindItem[],
    text: string,
    filter: FindFilter,
): readonly FindItem[] => {
    const words = text.toLocaleLowerCase().split(/\s+/).filter(Boolean);
    if (words.length === 0 && filter === "all") return [];
    return items.filter(
        (item) =>
            passes(item, filter) &&
            words.every((w) =>
                `${item.title} ${item.brief}`.toLocaleLowerCase().includes(w),
            ),
    );
};

// The cards that light up, each with how many of its matches lie inside it.
const litCards = (hits: readonly FindItem[]): ReadonlyMap<string, number> =>
    hits.reduce(
        (m, h) =>
            new Map(m).set(
                h.card,
                (m.get(h.card) ?? 0) + (h.id === h.card ? 0 : 1),
            ),
        new Map<string, number>(),
    );
